import { useState, useEffect, useContext, useMemo } from "react";
import { motion } from "framer-motion";
import Select from "react-select";
import { Helmet } from "react-helmet-async";
import { AppLayout } from "../../../layout/AppLayout";
import { Dot } from "../../../components/svg/GeneralIcons";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetAudience } from "../../../api/audience/useGetAudience";
import { useGenerateBrand } from "../../../api/useGenerateBrand";
import { useSelectBranch } from "../../../api/audience/useSelectBranch";
import { UserContext } from "../../../components/context/UseContext";
import { Capitalize } from "../../../utils";
import { RmcContext } from "../../../components/context/RmcContext";
import clsx from "clsx";
import { Loader } from "../../../components/loader/Loader";
import { toast } from "sonner";

function RmcLandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [church, setChurch] = useState(null);
  const { GetAudience, loading: loadingAudience } = useGetAudience();
  const searchParams = new URLSearchParams(location.search);
  const brandId = searchParams.get("id");
  const userId = searchParams.get("userId");
  const { profile, token } = useContext(UserContext);
  const { setWorldBranch } = useContext(RmcContext);
  const hashedData = localStorage.getItem("arena_hashed_d");
  const { generateBrand, data, loading } = useGenerateBrand();
  const { selectBranch, loading: loadingBranch } = useSelectBranch();

  useEffect(() => {
    if (brandId) {
      GetAudience(brandId, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const expires = searchParams.get("expires");
    const signature = searchParams.get("signature");
    if (expires && signature && userId) {
      generateBrand({
        token: token,
        brand_id: brandId,
        user_id: userId ?? profile.id,
        is_link: true,
        route: `?data=${hashedData}&expires=${expires}&signature=${signature}`,
      });
    } else if (!expires && !signature) {
      generateBrand({
        token: token,
        brand_id: brandId,
        is_link: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, brandId, profile?.user_id]);

  // const progress = calculateBadgeProgress(data.points, data.audience_badges);

  const churchOptions = useMemo(() => {
    if (!data?.data?.brand?.branches) {
      return [];
    }
    return data.data.brand.branches.map((branch) => ({
      value: branch.id,
      label: branch.name,
    }));
  }, [data?.data?.brand?.branches]);

  const selectedBranchOption = useMemo(() => {
    if (!data?.data?.selected_branch) return null;

    return churchOptions.find(
      (option) =>
        option.label.toLowerCase() === data.data.selected_branch.toLowerCase(),
    );
  }, [data?.data?.selected_branch, churchOptions]);

  useEffect(() => {
    if (selectedBranchOption) {
      setChurch(selectedBranchOption);
      setWorldBranch(selectedBranchOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchOption]);

  return (
    <>
      <Helmet>
        <title>
          {data?.data?.brand?.name
            ? `${data.data.brand.name} - RMC World | Arena`
            : "RMC World - Join the Ultimate Gaming Experience | Arena"}
        </title>
        <meta
          name="description"
          content={
            data?.data?.brand.description ||
            "Welcome to RMC World! Join exciting trivia games, compete with others, and win amazing rewards in the ultimate gamification platform."
          }
        />
        <meta
          name="keywords"
          content={`RMC World, ${
            data?.data?.brand?.name || "gaming"
          }, trivia, competition, rewards, arena, church gaming, religious games, faith-based gaming`}
        />
        <meta
          property="og:title"
          content={
            data?.data?.brand?.name
              ? `${data.data.brand.name} - RMC World | Arena`
              : "RMC World - Join the Ultimate Gaming Experience | Arena"
          }
        />
        <meta
          property="og:description"
          content={
            data?.data?.brand?.description ||
            "Welcome to RMC World! Join exciting trivia games, compete with others, and win amazing rewards in the ultimate gamification platform."
          }
        />
        <meta
          property="og:image"
          content={data?.data?.brand?.image_url || "/image/church.png"}
        />
        <meta
          property="og:url"
          content={`https://arenaworld.fun/rmc-landing-page?id=${brandId}`}
        />
        <meta
          property="twitter:title"
          content={
            data?.data?.brand?.name
              ? `${data.data.brand.name} - RMC World | Arena`
              : "RMC World - Join the Ultimate Gaming Experience | Arena"
          }
        />
        <meta
          property="twitter:description"
          content={
            data?.data?.brand?.description ||
            "Welcome to RMC World! Join exciting trivia games, compete with others, and win amazing rewards in the ultimate gamification platform."
          }
        />
        <meta
          property="twitter:image"
          content={data?.data?.brand?.image_url || "/image/church.png"}
        />
        <link
          rel="canonical"
          href={`https://arenaworld.fun/rmc-landing-page?id=${brandId}`}
        />
      </Helmet>
      <AppLayout bg={"!bg-[#111522]"} noNav={true}>
        <div className="relative pb-14">
          {loading ? (
            <div className="px-5">
              <div className="h-[200px] bg-gray-600 rounded-lg animate-pulse mt-8"></div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 3, delay: 0.4 }}
              className="relative rounded-b-[25px]"
            >
              <img
                className="w-full object-contain rounded-b-[25px]"
                src={
                  data?.data?.brand?.image_url &&
                  data?.data?.brand?.image_url !== "default.png"
                    ? data?.data?.brand?.image_url
                    : "/image/church.png"
                }
                alt="banner"
                loading="lazy"
              />
            </motion.div>
          )}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 3, delay: 0.4 }}
            className="px-5"
          >
            {loading ? (
              <div className="h-[20px] bg-gray-600 rounded-lg animate-pulse mt-12"></div>
            ) : (
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="tracking-tighter text-3xl font-semibold text-white mt-12 mx-0"
              >
                Welcome to {data?.data?.brand?.name}
              </motion.h3>
            )}

            {loading ? (
              <div className="mt-5">
                <div className="h-[10px] bg-gray-600 rounded-lg animate-pulse mt-2"></div>
                <div className="h-[10px] bg-gray-600 rounded-lg animate-pulse mt-2"></div>
                <div className="h-[10px] bg-gray-600 rounded-lg animate-pulse mt-2"></div>
              </div>
            ) : (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="text-base capitalize font-medium mt-5 text-[#FFFFFFBF]"
              >
                {data?.data?.brand?.description ||
                  "Get ready to win exciting rewards"}
              </motion.p>
            )}

            {loading ? (
              <div className="h-[50px] bg-gray-600 rounded-lg animate-pulse my-7"></div>
            ) : data?.data?.brand?.branches?.length > 0 ? (
              <Select
                className="basic-single custom-scrollbar rmc-select"
                classNamePrefix="select-Bank"
                defaultValue={
                  data?.data?.selected_branch !== null
                    ? selectedBranchOption
                    : churchOptions[0]
                }
                isClearable={data?.data?.selected_branch === null}
                isRtl={false}
                placeholder={
                  data?.data?.brand.select_text ?? "Select Church from list"
                }
                isSearchable={data?.data?.selected_branch === null}
                isDisabled={data?.data?.selected_branch !== null}
                onChange={(selectedOption) => {
                  setChurch(selectedOption);

                  if (selectedOption) {
                    setWorldBranch(selectedOption);
                  } else {
                    setWorldBranch(null);
                    localStorage.removeItem("world_branch");
                  }
                }}
                options={churchOptions}
                value={
                  data?.data?.selected_branch !== null
                    ? selectedBranchOption
                    : church
                }
                styles={{
                  singleValue: (provided) => ({
                    ...provided,
                    color: "white",
                  }),
                  control: (provided) => ({
                    ...provided,
                    opacity: data?.data?.selected_branch !== null ? 0.6 : 1,
                  }),
                }}
              />
            ) : null}

            <div className="mt-16 flex flex-col gap-y-3">
              {loading ? (
                <>
                  <div className="h-[20px] bg-gray-600 rounded-lg animate-pulse mt-2"></div>
                  <div className="h-[20px] bg-gray-600 rounded-lg animate-pulse "></div>
                  <div className="h-[20px] bg-gray-600 rounded-lg animate-pulse "></div>
                  <div className="h-[20px] bg-gray-600 rounded-lg animate-pulse "></div>
                </>
              ) : (
                data?.data?.brand?.details?.map((x, i) => {
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-4 h-4 mt-0.5">
                        <Dot />
                      </span>
                      <p className="text-[#FFFFFFBF] text-sm">
                        {Capitalize(x.detail)}
                      </p>
                    </div>
                  );
                })
              )}
            </div>
            {data?.data && (
              <>
                <button
                  disabled={
                    loading || loadingAudience || loadingBranch || !church
                  }
                  onClick={async () => {
                    if (!church) return;

                    if (data?.data?.selected_branch !== null) {
                      if (
                        data.data.selected_branch?.toLowerCase() !==
                        church?.label?.toLowerCase()
                      ) {
                        toast.error(
                          `You cannot switch church branch. Your church branch is ${data.data.selected_branch}`,
                        );
                        return;
                      }
                      navigate(`home?id=${brandId}`);
                    } else {
                      const success = await selectBranch(brandId, church.value);
                      if (success) {
                        navigate(`home?id=${brandId}`);
                      }
                    }
                  }}
                  className={clsx(
                    loading || loadingAudience || loadingBranch || !church
                      ? "opacity-50 cursor-not-allowed"
                      : "",
                    "mt-10 mb-8 h-[60px] w-full font-semibold text-lg rounded-[10px] flex items-center justify-center",
                  )}
                  style={{
                    backgroundColor:
                      data?.data?.brand?.secondary_color ?? "#FFD700",
                  }}
                >
                  {loadingBranch ? (
                    <Loader type="spinningBubbles" color="#000" />
                  ) : (
                    "Get Started"
                  )}
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="h-[60px] w-full font-semibold text-lg bg-transparent border-2 border-[#FFFFFF40] text-white rounded-[10px] flex items-center justify-center"
                >
                  {data?.data?.brand?.leave_text ?? "Leave RMC World"}
                </button>
              </>
            )}
          </motion.div>
        </div>
      </AppLayout>
    </>
  );
}

export default RmcLandingPage;
